import { ComponentProps } from 'react';
import { Control, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type STextFieldProps = ComponentProps<'s-text-field'>;
type STextAreaProps = ComponentProps<'s-text-area'>;

type TextFieldControlledWithLabelProps<T> = Omit<
    STextFieldProps & STextAreaProps,
    'name' | 'value' | 'onChange' | 'onInput' | 'error'
> & {
    control: Control<T>;
    name: Path<T>;
    annotated?: boolean;
    labelHidden?: boolean;
    tooltip?: string;
    multiline?: boolean;
};

export default function TextFieldControlledWithLabel<T>({
    control,
    name,
    label,
    annotated = false,
    labelHidden,
    tooltip,
    multiline,
    ...props
}: TextFieldControlledWithLabelProps<T>) {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => {
                const sharedProps = {
                    label: annotated ? undefined : label,
                    labelAccessibilityVisibility: labelHidden ? ('exclusive' as const) : undefined,
                    name,
                    value: value?.toString() ?? '',
                    error: error?.message ? t(error.message as never) : undefined,
                    onBlur,
                };

                const field = multiline ? (
                    <s-text-area
                        {...(props as STextAreaProps)}
                        {...sharedProps}
                        onInput={(event) => onChange(event.currentTarget.value)}
                    />
                ) : (
                    <s-text-field
                        {...(props as STextFieldProps)}
                        {...sharedProps}
                        onInput={(event) => onChange(event.currentTarget.value)}
                    />
                );

                if (!annotated) return field;

                return (
                    <div className="grid grid-cols-2 items-center gap-2">
                        <div className="flex flex-row items-center gap-2">
                            <span>{label}</span>
                            {!!tooltip && (
                                <>
                                    <s-icon type="info" interestFor={`tooltip-${name}`}></s-icon>
                                    <s-tooltip id={`tooltip-${name}`}>{tooltip}</s-tooltip>
                                </>
                            )}
                        </div>
                        {field}
                    </div>
                );
            }}
        />
    );
}
